import { Button, Dialog, DialogContent } from "@mui/material"
import axios from "axios"
import dayjs from "dayjs"
import { t } from "i18next"
import React, { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Route, Routes, useNavigate } from "react-router-dom"
import { Loader } from "../../components/Loader"
import { Selector, SelectorType } from "../../components/Selector"
import { useAppContext } from "../../contexts/AppContext"
import { convertToDateWithUtc, getLocal, getSignedUrl, showErrorMessage } from "../../helper/helper"
import { colorLight, colorYellowLight } from "../../rsc/colors/colors"
import { firebaseApp } from "../login/firebaseInit"
import { PempemInvoiceDetail } from "./PempemInvoiceDetail"
import { VATInvoiceDetail } from "./VATInvoiceDetail"

export type InvoiceREST = {
    fileName: string
    fileNameShortened: string
    url: string
    total: any
    date: string
    metadata: any
}

const buildYearFromTimestamp = (): SelectorType[] => {
    const years: SelectorType[] = []
    let timestamp = 1704067200000; //Timestamp of 01/01/2024
    let startDate = new Date(timestamp);
    let startYear = startDate.getUTCFullYear();
    let currentYear = new Date().getUTCFullYear();

    for (let year = startYear; year <= currentYear; year++) {
        years.push({ id: year, value: year.toString() });
    }

    return years
}

const YearSelectorData: SelectorType[] = buildYearFromTimestamp()

type CSVData = {
    data: any
    invoice: InvoiceREST
}

export function PempemInvoiceList() {

    const { millSelected } = useAppContext()

    const buildMonth = () => {
        var allMonths: SelectorType[] = [{ id: 0, value: t('all') }]
        const months = Array.from({ length: 12 }, (_, i) => dayjs().locale(getLocal()).month(i).format('MMMM'));
        months.forEach((m, i) => {
            allMonths.push({ id: i + 1, value: m })
        })
        return allMonths
    }

    const MonthSelectorData: SelectorType[] = buildMonth()


    const token = useSelector((state: any) => state.token.value)

    const [filteredInvoices, setFilteredInvoices] = useState<InvoiceREST[]>([])
    const [loading, setLoading] = useState<boolean>()
    const [monthSelected, setMonthSelected] = useState<SelectorType>(MonthSelectorData[0])
    const [yearSelected, setYearStelected] = useState<SelectorType>(YearSelectorData[YearSelectorData.length - 1])
    const [invoiceCsv, setInvoiceCsv] = useState<CSVData>(undefined)

    const invoices = useRef<InvoiceREST[]>([])
    const invoiceSelected = useRef<InvoiceREST>(undefined)
    const pagingOffset = useRef(0)

    const [invoiceClose, setInvoiceClose] = useState<boolean | undefined>(undefined)

    const navigate = useNavigate()

    useEffect(() => {
        setLoading(true)
        const collection = firebaseApp.firestore().collection('niagaEnterprise')
        const unsubscribe = collection.onSnapshot((changes) => {
            var doc = changes.docs.find(d => d.id === "flags").data()
            var isEnbale = doc.invoiceClosed
            setInvoiceClose(isEnbale)
        })
        return () => {
            unsubscribe()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (millSelected) {
            pagingOffset.current = 0
            invoices.current = []
            fetchInvoices()
        }
        // eslint-disable-next-line
    }, [millSelected, monthSelected, yearSelected])

    function setInvoicesWithFilter(newInvoices: InvoiceREST[]) {
        var filteredInvoices = newInvoices

        setFilteredInvoices(filteredInvoices.sort((a: InvoiceREST, b: InvoiceREST) => {
            return b.fileNameShortened.localeCompare(a.fileNameShortened)
        }
        ))
    }

    function isVAT(invoice: InvoiceREST) {
        const split = invoice.fileName.split('.')
        if (split) {
            const size = split.length
            return split[size - 1] === 'pdf'
        }
    }

    function isCSV(invoice: InvoiceREST) {
        const split = invoice.fileName.split('.')
        if (split) {
            const size = split.length
            return split[size - 1] === 'csv'
        }
    }

    function goToInvoiceDetail(invoice: InvoiceREST) {
        invoiceSelected.current = invoice
        setLoading(true)
        getSignedUrl(millSelected.id, invoice.url).then(url => {
            const path = encodeURIComponent(url)
            navigate(`/explorer/invoice_pempem_detail?orgId=${millSelected.id}&pdfUrl=${path}`)
        })
            .catch(e => showErrorMessage(e))
            .finally(() => setLoading(false))
    }

    function InvoiceHeader() {
        return <tr>
            <th>{t('name')}</th>
            <th>{t('date')}</th>
            <th>{t('total')}</th>
            <th>{t('type')}</th>
        </tr>
    }

    function InvoiceRow(invoice: InvoiceREST) {
        const vat = isVAT(invoice)
        return (
            <tr style={{ background: vat ? colorYellowLight() : colorLight() }} key={invoice.fileName} onClick={() => goToInvoiceDetail(invoice)} className="cursor-pointer hoverGreenPale">
                <td>{invoice.fileNameShortened}</td>
                <td>{convertToDateWithUtc(invoice.date)}</td>
                <td>{invoice.total.IndoFormat()}</td>
                <td>{isVAT(invoice) ? t('vat') : 'PEMPEM'}</td>
            </tr>
        )
    }

    function InvoicesTab() {
        return (
            <div style={{ padding: "0 28px" }}>
                <table className="pem-table" style={{ width: "100%", marginTop: 20 }}>
                    <thead>
                        {InvoiceHeader()}
                    </thead>
                    <tbody>
                        {filteredInvoices.map((i) => InvoiceRow(i))}
                    </tbody>
                </table>
            </div>
        )
    }

    function fetchInvoices() {
        if (millSelected.id) {
            setLoading(true)
            const timeParam = `year=${yearSelected.id}${monthSelected.id !== 0 ? `&month=${monthSelected.id}` : ''}`
            axios.get(`${process.env.REACT_APP_BASE_URL_PUBLIC}/invoicing/api/list?orgIds=${millSelected.id}&${timeParam}`, {
                withCredentials: false,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    "x-app-id": process.env.REACT_APP_APP_ID,
                    "x-app-name": "NFM"
                }
            }).then((data) => {
                const inv: InvoiceREST[] = data.data[0].invoices.map(i => { return { fileName: i.fileName, url: i.url, total: i.total, date: i.date, metadata: i.metadata, fileNameShortened: i.fileNameShortened } })
                invoices.current = inv
                setInvoicesWithFilter(inv)
            }).catch(e => {
                if (e) {
                    showErrorMessage(e)
                } else {
                    showErrorMessage(t('an_error_occured'))
                }
            })
                .finally(() => {
                    setLoading(false)
                })
        }

    }

    const onErrorLoading = (orgId: string) => {
        showErrorMessage(t('error_invoice_expired'))
        fetchInvoices()
    }


    const getCsv = (invoice: InvoiceREST) => {
        setLoading(true)
        const path = invoice.url
        axios.get(path, {
            withCredentials: false,
        }).then((data) => {
            setInvoiceCsv({
                invoice: invoice,
                data: data.data
            })
        }).catch(e => {
            console.log(e)
        })
            .finally(() => {
                setLoading(false)
            })
    }

    const downloadCsv = () => {
        const blob = new Blob([invoiceCsv.data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${invoiceCsv.invoice.fileNameShortened}.csv`
        link.click();
    }

    const parseCSV = (csv: string) => {
        if (!csv) return undefined
        const rows = csv.split("\n");
        const rowsParsed = rows.map((row) => row.split(",")).filter(r => r.length > 1)

        return <div>
            {
                rowsParsed.map((row, i) => {
                    const className = i === 0 ? "border-black-solid-header text-color-primary greenPaleBgColor" : "border-black-solid-row"
                    return <div style={{ display: "flex" }}>
                        <div className={className} style={{ width: '210px', border: "1px solid black" }}>{row[0]}</div>
                        <div className={className} style={{ width: '100px' }}>{row[1]}</div>
                        <div className={className} style={{ width: '100px' }}>{row[2]}</div>
                        <div className={className} style={{ width: '100px' }}>{row[3]}</div>
                        <div className={className} style={{ width: '100px' }}>{row[4]}</div>
                        <div className={className} style={{ width: '100px' }}>{row[5]}</div>
                        <div className={className} style={{ width: '100px' }}>{row[6]}</div>
                        <div className={className} style={{ width: '100px' }}>{row[7]}</div>
                    </div>
                })
            }
        </div>
    };

    const InvoiceListView = () => {
        return (
            <div>
                <h1 className="header">{t("invoices")}</h1>
                <div style={{ verticalAlign: "bottom", display: "flex", height: "auto", paddingLeft: 28, paddingRight: 28, marginTop: 15, flexShrink: "0", alignItems: "end", gap: "5px" }}>
                    <Selector currentValue={monthSelected} list={MonthSelectorData} onSelectionChange={setMonthSelected} label={t('month')} />
                    <Selector currentValue={yearSelected} list={YearSelectorData} onSelectionChange={setYearStelected} label={t('year')} />
                </div>
                {InvoicesTab()}
                <Dialog
                    open={invoiceCsv !== undefined}
                    onClose={() => { setInvoiceCsv(undefined) }}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description">
                    <DialogContent>
                        <div style={{ background: "white", maxHeight: "600px", display: "flex", flexDirection: "column", gap: "10px" }}>
                            <p className="text-color-primary-22" style={{ marginBottom: '20px' }}>{invoiceCsv?.invoice?.fileNameShortened}</p>
                            <div style={{ overflow: "scroll", display: 'flex', gap: '10px', minHeight: '250px' }}>
                                {
                                    parseCSV(invoiceCsv?.data)
                                }
                            </div>
                            <Button style={{ width: "150px", alignSelf: 'center' }} onClick={downloadCsv} variant="contained">
                                {t('download')}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        )
    }

    return (
        <Routes>
            <Route path="/" element={
                loading ?
                    <Loader isLoading={loading} />
                    :
                    invoiceClose ?
                        <div style={{
                            display: "flex",
                            border: "1px solid black",
                            borderRadius: "5px",
                            position: "relative",
                            flex: "1",
                            backgroundColor: colorYellowLight(),
                            minHeight: "100px",
                            margin: "30px 28px 0px 28px",
                            justifyContent: "center",
                            alignItems: "center"
                        }}>
                            <p style={{ margin: "0px", fontSize: "32px", lineHeight: '32px' }}>{t('invoice_page_closed')}</p>
                        </div>
                        : <InvoiceListView />
            }></Route>
            <Route path="/invoice_vat_detail" element={<VATInvoiceDetail invoice={invoiceSelected.current} onErrorLoading={onErrorLoading} />} />
            <Route path="/invoice_pempem_detail" element={<PempemInvoiceDetail onErrorLoading={onErrorLoading} />} />
        </Routes >
    )
}